import { ChatbotConfig, useChatbotConfigStore } from '../stores/chatbotConfigStore';
import { supabase } from '../lib/supabase';

/**
 * Get the full chatbot config from the store
 * @param chatbotPublicId The chatbot public ID
 * @param forceRefresh Whether to bypass the cache and force a fresh fetch
 * @returns The chatbot config
 */
export async function getChatbotConfig(
  chatbotPublicId?: string, 
  forceRefresh = false
): Promise<ChatbotConfig | null> {
  return useChatbotConfigStore.getState().fetchConfig(chatbotPublicId, forceRefresh);
}

/**
 * Get a specific section of the chatbot config
 * @param sectionKey The key of the section to retrieve (e.g., 'colors', 'texts')
 * @param chatbotPublicId The chatbot public ID
 * @param forceRefresh Whether to bypass the cache and force a fresh fetch
 * @returns The specific section of the config, or null if not found
 */
export async function getChatbotConfigSection<T>(
  sectionKey: string,
  chatbotPublicId: string
): Promise<T | null> {
  try {
    // Récupérer la configuration du chatbot
    const { data, error } = await supabase
      .from('chatbot_configs')
      .select('settings')
      .eq('chatbot_public_id', chatbotPublicId)
      .single();
    
    if (error) {
      throw error;
    }
    
    if (!data || !data.settings) {
      return null;
    }
    
    // Vérifier si les paramètres existent à l'ancienne ou à la nouvelle structure
    if (data.settings[sectionKey]) {
      return data.settings[sectionKey] as T;
    } else if (sectionKey === 'order_tracking' && data.settings.enabled !== undefined) {
      // Code de compatibilité: Si on cherche order_tracking mais que les paramètres
      // sont directement à la racine (ancienne structure)
      return data.settings as unknown as T;
    } else {
      return null;
    }
  } catch (err) {
    throw err;
  }
}

/**
 * Update a specific section of the chatbot config
 * @param sectionKey The key of the section to update (e.g., 'colors', 'texts')
 * @param data The new data for the section
 * @param chatbotPublicId The chatbot public ID
 * @returns Whether the update was successful
 */
export async function updateChatbotConfigSection<T>(
  sectionKey: string,
  sectionData: T,
  chatbotPublicId: string
): Promise<boolean> {
  try {
    // Récupérer d'abord la configuration existante
    const { data: existingConfig, error: fetchError } = await supabase
      .from('chatbot_configs')
      .select('settings')
      .eq('chatbot_public_id', chatbotPublicId)
      .single();
    
    if (fetchError && fetchError.code !== 'PGRST116') {
      throw fetchError;
    }
    
    // Préparer les nouveaux paramètres
    const newSettings = existingConfig?.settings || {};
    
    // Mise à jour de compatibilité pour order_tracking
    if (sectionKey === 'order_tracking' && newSettings.enabled !== undefined && 
        Object.keys(newSettings).includes('shopify_enabled')) {
      // Ancienne structure détectée, mettre à jour directement à la racine
      // Mettre à jour en mode compatible
      const { data, error } = await supabase
        .from('chatbot_configs')
        .update({ settings: { ...sectionData } })
        .eq('chatbot_public_id', chatbotPublicId);
      
      if (error) {
        throw error;
      }
      
      return true;
    }
    
    // Structure normale - mettre à jour la section spécifique
    newSettings[sectionKey] = sectionData;
    
    // Mettre à jour la configuration
    const { data, error } = await supabase
      .from('chatbot_configs')
      .update({ settings: newSettings })
      .eq('chatbot_public_id', chatbotPublicId);
    
    if (error) {
      throw error;
    }
    
    return true;
  } catch (err) {
    throw err;
  }
}

/**
 * Clear the config cache for a specific chatbot or all chatbots
 * @param chatbotPublicId Optional chatbot public ID to clear cache for a specific chatbot
 */
function clearConfigCache(chatbotPublicId?: string): void {
  useChatbotConfigStore.getState().clearConfig(chatbotPublicId);
}

// Re-export the ChatbotConfig type for convenience
export type { ChatbotConfig };
